import { CSSProperties, useEffect, useState } from 'react';
import ModalSymphony from 'elements/ModalSymphony';
import { getCampaign_api } from 'api/campaignApi';
import { CampaignDetails } from 'modules/Types';
import Colors from 'modules/Colors';
import { styx } from 'modules/Utils';
import Loader from 'elements/Loader';

interface AgentConversationModalProps {
    isOpen: boolean;
    onClose: () => void;
    campaignId: number;
}

interface ConversationMessage {
    role?: 'user' | 'assistant' | 'developer';
    content?: string | Array<{ text: string; type: string; logprobs?: any[]; annotations?: any[] }>;
    timestamp?: string;
    type?: string;
    summary?: Array<{ text: string; type: string }>;
    name?: string;
    arguments?: string;
    output?: string;
    id?: string;
}

const AgentConversationModal = ({
    isOpen,
    onClose,
    campaignId
}: AgentConversationModalProps) => {
    const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentIteration, setCurrentIteration] = useState<number | null>(null);
    const [lastUpdated, setLastUpdated] = useState<string | null>(null);

    const fetchConversationHistory = async () => {
        if (!campaignId) return;
        
        setIsLoading(true);
        try {
            const campaignData: CampaignDetails | null = await getCampaign_api(campaignId);
            if (campaignData?.agent_notes?.conversation_history) {
                setConversationHistory(campaignData.agent_notes.conversation_history);
                setCurrentIteration(campaignData.agent_notes.current_iteration || null);
                setLastUpdated(campaignData.agent_notes.last_iteration_updated_at || null);
            }
        } catch (error) {
            console.error('Error fetching conversation history:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch conversation history when modal opens
    useEffect(() => {
        if (isOpen) {
            fetchConversationHistory();
        }
    }, [isOpen, campaignId]);

    // Auto-refresh conversation history every 5 seconds when modal is open
    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            fetchConversationHistory();
        }, 5000);

        return () => clearInterval(interval);
    }, [isOpen, campaignId]);

    const formatFunctionCall = (message: ConversationMessage) => {
        const args = message.arguments ? JSON.parse(message.arguments) : {};
        return `🔧 Function Call: ${message.name}\nArguments: ${JSON.stringify(args, null, 2)}`;
    };

    const formatFunctionOutput = (message: ConversationMessage) => {
        let output = message.output || '';
        try {
            const parsed = JSON.parse(output);
            output = JSON.stringify(parsed, null, 2);
        } catch {
            // Keep original output if not valid JSON
        }
        return `📊 Function Output:\n${output}`;
    };

    const formatReasoningMessage = (message: ConversationMessage) => {
        if (!message.summary || !Array.isArray(message.summary)) {
            return 'AI Agent is thinking...';
        }
        
        return message.summary
            .map(item => item.text)
            .join('\n\n');
    };

    const getMessageContent = (message: ConversationMessage): string => {
        if (message.content) {
            // Handle case where content is an array of content objects
            if (Array.isArray(message.content)) {
                return message.content
                    .map(item => {
                        if (typeof item === 'string') {
                            return item;
                        }
                        if (item && typeof item === 'object' && 'text' in item) {
                            return item.text;
                        }
                        return '';
                    })
                    .join('\n\n');
            }
            return message.content;
        }
        
        if (message.type === 'reasoning') {
            return formatReasoningMessage(message);
        }
        
        if (message.type === 'function_call') {
            return formatFunctionCall(message);
        }
        
        if (message.type === 'function_call_output') {
            return formatFunctionOutput(message);
        }
        
        return 'Unknown message type';
    };

    const getMessageRole = (message: ConversationMessage): 'user' | 'assistant' => {
        if (message.role === 'user') {
            return 'user';
        }
        
        if (message.role === 'developer' || message.role === 'assistant') {
            return 'assistant';
        }
        
        if (message.type === 'reasoning' || message.type === 'function_call' || message.type === 'function_call_output') {
            return 'assistant';
        }
        
        return 'assistant';
    };

    const getMessageIcon = (message: ConversationMessage): string => {
        if (message.role === 'user') {
            return '👤';
        }
        
        if (message.role === 'developer') {
            return '👨‍💻';
        }
        
        if (message.type === 'reasoning') {
            return '🧠';
        }
        
        if (message.type === 'function_call') {
            return '🔧';
        }
        
        if (message.type === 'function_call_output') {
            return '📊';
        }
        
        return '🤖';
    };

    const getMessageLabel = (message: ConversationMessage): string => {
        if (message.role === 'user') {
            return 'User';
        }
        
        if (message.role === 'developer') {
            return 'System';
        }
        
        if (message.type === 'reasoning') {
            return 'AI Reasoning';
        }
        
        if (message.type === 'function_call') {
            return 'Function Call';
        }
        
        if (message.type === 'function_call_output') {
            return 'Function Result';
        }
        
        return 'AI Agent';
    };

    const renderMessage = (message: ConversationMessage, index: number) => {
        const role = getMessageRole(message);
        const isUser = role === 'user';
        const content = getMessageContent(message);
        const icon = getMessageIcon(message);
        const label = getMessageLabel(message);
        
        // Skip empty reasoning messages
        if (message.type === 'reasoning' && (!message.summary || message.summary.length === 0)) {
            return null;
        }
        
        return (
            <div 
                key={index}
                className="conversationMessage"
                style={styx(
                    sty.conversationMessage,
                    isUser ? sty.userMessage : sty.assistantMessage,
                    message.type === 'function_call' || message.type === 'function_call_output' ? sty.technicalMessage : {}
                )}
            >
                <div className="messageRole" style={sty.messageRole}>
                    {icon} {label}
                </div>
                <div className="messageContent" style={styx(
                    sty.messageContent,
                    message.type === 'function_call' || message.type === 'function_call_output' ? sty.technicalContent : {}
                )}>
                    {content}
                </div>
                {message.timestamp && (
                    <div className="messageTimestamp" style={sty.messageTimestamp}>
                        {new Date(message.timestamp).toLocaleString()}
                    </div>
                )}
            </div>
        );
    };

    return (
        <ModalSymphony
            className="agentConversationModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Agent Conversation History"
            subtitle={currentIteration ? `Current Iteration: ${currentIteration}` : 'Viewing agent reasoning process'}
        >
            <div className="agentConversationContent" style={sty.modalContent}>
                {lastUpdated && (
                    <div className="lastUpdated" style={sty.lastUpdated}>
                        Last updated: {new Date(lastUpdated).toLocaleString()}
                    </div>
                )}
                
                {isLoading && conversationHistory.length === 0 ? (
                    <div style={sty.loaderContainer}>
                        <Loader size={32} title="Loading conversation history..." />
                    </div>
                ) : conversationHistory.length === 0 ? (
                    <div style={sty.emptyState}>
                        <div style={sty.emptyStateIcon}>💬</div>
                        <div style={sty.emptyStateTitle}>No conversation history yet</div>
                        <div style={sty.emptyStateDescription}>
                            The agent hasn't started processing this campaign yet.
                        </div>
                    </div>
                ) : (
                    <div className="conversationList" style={sty.conversationList}>
                        {conversationHistory.map((message, index) => renderMessage(message, index)).filter(Boolean)}
                        {isLoading && (
                            <div style={sty.refreshIndicator}>
                                Refreshing...
                            </div>
                        )}
                    </div>
                )}
            </div>
        </ModalSymphony>
    );
};

const sty: Record<string, CSSProperties> = {
    modalContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        maxHeight: '60vh',
        overflow: 'hidden',
    },
    lastUpdated: {
        fontSize: 12,
        color: Colors.greyDark,
        textAlign: 'center',
        padding: '8px 0',
        borderBottom: `1px solid ${Colors.greyLight}`,
    },
    conversationList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        overflowY: 'auto',
        padding: '0 4px',
        flex: 1,
    },
    conversationMessage: {
        padding: 16,
        borderRadius: 8,
        border: `1px solid ${Colors.greyLight}`,
    },
    userMessage: {
        backgroundColor: Colors.purple + '10',
        borderColor: Colors.purple + '30',
    },
    assistantMessage: {
        backgroundColor: Colors.green + '10',
        borderColor: Colors.green + '30',
    },
    technicalMessage: {
        backgroundColor: Colors.blue + '10',
        borderColor: Colors.blue + '30',
    },
    messageRole: {
        fontSize: 12,
        fontWeight: 600,
        marginBottom: 8,
        color: Colors.greyDark,
    },
    messageContent: {
        fontSize: 14,
        lineHeight: 1.5,
        color: Colors.textColor,
        whiteSpace: 'pre-wrap',
    },
    technicalContent: {
        fontFamily: 'monospace',
        fontSize: 12,
        backgroundColor: Colors.greyLight + '30',
        padding: 8,
        borderRadius: 4,
        border: `1px solid ${Colors.greyLight}`,
    },
    messageTimestamp: {
        fontSize: 11,
        color: Colors.greyDark,
        marginTop: 8,
        fontStyle: 'italic',
    },
    loaderContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 40,
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        textAlign: 'center',
    },
    emptyStateIcon: {
        fontSize: 48,
        marginBottom: 16,
    },
    emptyStateTitle: {
        fontSize: 18,
        fontWeight: 600,
        color: Colors.textColor,
        marginBottom: 8,
    },
    emptyStateDescription: {
        fontSize: 14,
        color: Colors.greyDark,
        maxWidth: 300,
    },
    refreshIndicator: {
        fontSize: 12,
        color: Colors.greyDark,
        textAlign: 'center',
        padding: 8,
        fontStyle: 'italic',
    },
};

export default AgentConversationModal; 